/**
 * Formatadores de exibicao.
 *
 * Todos toleram valor ausente: 48% da base nao tem e-mail, 40% nao tem CPF, e a
 * tela precisa mostrar a ausencia em vez de quebrar.
 */

/** Traco de ausencia, para nao deixar celula vazia sem explicacao. */
export const EMPTY = '—';

/** Formata CPF como `000.000.000-00`. Devolve o valor cru se nao tiver 11 digitos. */
export function formatCpf(value: string | null | undefined): string {
  if (!value) return EMPTY;
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 11) return value;
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

/** Formata CEP como `00000-000`. */
export function formatCep(value: string | null | undefined): string {
  if (!value) return EMPTY;
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 8) return value;
  return digits.replace(/(\d{5})(\d{3})/, '$1-$2');
}

/** Formata telefone fixo ou celular com DDD. */
export function formatPhone(value: string | null | undefined): string {
  if (!value) return EMPTY;
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  if (digits.length === 10) return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  return value;
}

/** Formata `aaaa-mm-dd` como `dd/mm/aaaa`, sem passar por `Date` (evita fuso). */
export function formatDate(value: string | null | undefined): string {
  if (!value) return EMPTY;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  return `${match[3]}/${match[2]}/${match[1]}`;
}

/** Formata `aaaa-mm-dd hh:mm:ss` como `dd/mm/aaaa hh:mm`. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return EMPTY;
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(value);
  if (!match) return formatDate(value);
  return `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]}`;
}

/** Formata valor monetario em reais. */
export function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return EMPTY;
  const numeric = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(numeric)) return EMPTY;
  return numeric.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Partes que nao carregam inicial: preposicoes de nome composto. */
const CONECTORES = new Set(['da', 'de', 'do', 'das', 'dos', 'e']);

/**
 * Iniciais para avatar, no maximo duas letras.
 *
 * O nome vindo do legado costuma trazer apelido entre parenteses na frente e
 * codigo no fim — "(DIDA) LEONILSON SANTOS DA SILVA - 8840". Tomar a primeira e
 * a ultima palavra ali dava "(8". Parenteses, pontuacao e numeros nao viram
 * inicial; conectores tambem nao.
 */
export function initials(name: string | null | undefined): string {
  if (!name?.trim()) return '?';

  const parts = name
    .trim()
    .split(/\s+/)
    .filter((part) => !part.startsWith('('))
    .map((part) => part.replace(/[^\p{L}].*$/u, ''))
    .filter((part) => part !== '' && !CONECTORES.has(part.toLowerCase()));

  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  const iniciais = (first + last).toUpperCase();

  return iniciais || '?';
}

/** Devolve o texto ou o traco de ausencia. */
export function orEmpty(value: string | null | undefined): string {
  return value?.trim() ? value : EMPTY;
}
